import { useMemo } from 'react';
import dayjs from 'dayjs';
import { HandoffHubV1 } from '@frontend/api-handoff-hub';
import { IntegrationsQueries } from '@frontend/api-integrations';
import { useAppScopeStore } from '@frontend/scope';
import { useMessagingAIRCustomizationFlag, useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { ONBOARDED_DURATION_DAYS, TRUE_LARK_ELIGIBLE_PMS_REGEX } from '../constants';
import { checkSyncAppEligibilityForTruelark } from '../utils';

export const useIsTrueLarkEligible = ({
  locationIds,
  enabled = true,
}: {
  locationIds?: string[];
  enabled?: boolean;
}) => {
  const { selectedLocationIds: scopedLocationIds } = useAppScopeStore();

  const selectedLocationIds = useMemo(() => locationIds || scopedLocationIds || [], [locationIds, scopedLocationIds]);

  const { truelarkMessagingAIFlagValueMap } = useMessagingAIRCustomizationFlag(selectedLocationIds);
  const { truelarkVoiceAIFlagValueMap } = useVoiceAIRCustomizationFlag(selectedLocationIds);

  const { trueLarkUnsubscribedLocationIds } = useMemo(() => {
    const unsubscribedLocationIds = selectedLocationIds.filter((locationId) => {
      const hasMessaging = truelarkMessagingAIFlagValueMap[locationId];
      const hasVoice = truelarkVoiceAIFlagValueMap[locationId];
      return !hasMessaging && !hasVoice;
    });

    return {
      trueLarkUnsubscribedLocationIds: unsubscribedLocationIds,
    };
  }, [selectedLocationIds, truelarkMessagingAIFlagValueMap, truelarkVoiceAIFlagValueMap]);

  const onboardedDurationCheckQueries = HandoffHubV1.Queries.useGetHandoffSnapshotsByLocationIDQueries(
    trueLarkUnsubscribedLocationIds.map((locationId) => ({
      request: { locationId },
      options: {
        retry: false,
        enabled,
        select: (response) => {
          // Check if onboarding duration threshold has been met
          return response.handoffSnapshots?.some(({ submittedAt }) => {
            if (!submittedAt) return false;
            return dayjs(submittedAt as string).isBefore(dayjs().subtract(ONBOARDED_DURATION_DAYS, 'days'));
          });
        },
      },
    }))
  );

  const { onboardingDurationThresholdMetLocationIds, isLoadingOnboardedDurationCheck } = useMemo(() => {
    const isLoading = onboardedDurationCheckQueries.some((query) => query.isLoading);
    const thresholdMetLocationIds = onboardedDurationCheckQueries
      .map((query, index) => ({ locationId: trueLarkUnsubscribedLocationIds[index], meetsThreshold: query.data }))
      .filter((data) => data.meetsThreshold)
      .map((data) => data.locationId);

    return {
      isLoadingOnboardedDurationCheck: isLoading,
      onboardingDurationThresholdMetLocationIds: thresholdMetLocationIds,
    };
  }, [onboardedDurationCheckQueries, trueLarkUnsubscribedLocationIds]);

  const dataSourcesQueries = IntegrationsQueries.useGetMultiDataSources(
    {
      locationIds: onboardingDurationThresholdMetLocationIds,
    },
    {
      enabled: enabled && onboardingDurationThresholdMetLocationIds.length > 0,
      select: (response) => {
        return response.dataSources
          ?.filter((dataSource) => TRUE_LARK_ELIGIBLE_PMS_REGEX.test(dataSource.integration?.integrationName || ''))
          .map((dataSource) => ({
            locationId: dataSource.locationId || '',
            integrationName: dataSource.integration?.integrationName || '',
            sourceId: dataSource.sourceId || '',
          }));
      },
    }
  );

  const { isLoadingSource, sourceIds, sourceIdInfoMap } = useMemo(() => {
    const dataSourcesQueriesDataList = dataSourcesQueries.map((query) => query.data);
    const isLoading = dataSourcesQueries.some((query) => query.isLoading);

    const sourceIdInfoMap = dataSourcesQueriesDataList.reduce<
      Record<string, { locationId: string; integrationName: string }>
    >((acc, dataSources) => {
      dataSources?.forEach((dataSource) => {
        acc[dataSource.sourceId] = { locationId: dataSource.locationId, integrationName: dataSource.integrationName };
      });
      return acc;
    }, {});

    return {
      sourceIds: Object.keys(sourceIdInfoMap),
      isLoadingSource: isLoading,
      sourceIdInfoMap,
    };
  }, [dataSourcesQueries]);

  const syncAppInfoQueries = IntegrationsQueries.useGetMultiSyncAppInfo(
    { sourceIds },
    {
      enabled: enabled && sourceIds.length > 0,
    }
  );

  const { isSyncAppEligible, syncAppEligibleLocationIds, isLoadingSyncAppEligible } = useMemo(() => {
    const isLoading = syncAppInfoQueries.some((query) => query.isLoading);

    const syncAppEligibleSourceIds = syncAppInfoQueries
      .map((query, index) => ({
        syncAppInfo: query.data,
        sourceId: sourceIds[index],
      }))
      .filter(({ syncAppInfo, sourceId }) => {
        return (
          !!syncAppInfo &&
          checkSyncAppEligibilityForTruelark({
            name: syncAppInfo.name ?? sourceIdInfoMap[sourceId]?.integrationName ?? '',
            version: syncAppInfo.version,
          })
        );
      });

    const eligibleLocationIds = syncAppEligibleSourceIds.map(({ sourceId }) => sourceIdInfoMap[sourceId].locationId);

    return {
      isLoadingSyncAppEligible: isLoading,
      isSyncAppEligible: eligibleLocationIds.length > 0,
      syncAppEligibleLocationIds: eligibleLocationIds,
    };
  }, [sourceIdInfoMap, sourceIds, syncAppInfoQueries]);

  return {
    isEligible: isSyncAppEligible,
    syncAppEligibleLocationIds,
    isLoading: isLoadingOnboardedDurationCheck || isLoadingSource || isLoadingSyncAppEligible,
  };
};
